import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Download, Share2, Plus } from "lucide-react";

const DISMISS_KEY = "circashift.install.dismissed.v1";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}
function isStandalone() {
  if (typeof window === "undefined") return false;
  // iOS
  if (window.navigator && window.navigator.standalone) return true;
  // Others
  return window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Only reveal after a short delay so it doesn't fight the hero
      setTimeout(() => setVisible(true), 2500);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS: no beforeinstallprompt — surface a manual hint after a delay
    if (isIOS()) {
      const t = setTimeout(() => setVisible(true), 3000);
      return () => {
        window.removeEventListener("beforeinstallprompt", onBip);
        clearTimeout(t);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice && choice.outcome === "accepted") {
        localStorage.setItem(DISMISS_KEY, "installed");
      }
      setDeferred(null);
      setVisible(false);
    } else if (isIOS()) {
      setShowIosHint(true);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
    setShowIosHint(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[80] rounded-2xl border border-white/10 bg-[#0F141F]/95 backdrop-blur-xl shadow-2xl p-4 flex items-start gap-3"
      data-testid="install-prompt"
    >
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shrink-0 glow-indigo">
        <Download className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        {!showIosHint ? (
          <>
            <div className="font-display text-base font-semibold text-slate-100">Install CircaShift</div>
            <p className="text-slate-400 text-xs mt-0.5 leading-relaxed">
              Get it on your home screen — fullscreen, offline-ready, and one tap away.
            </p>
            <div className="mt-3 flex gap-2">
              <Button
                size="sm"
                onClick={install}
                data-testid="install-prompt-install"
                className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
              >
                <Download className="w-3.5 h-3.5 mr-1" /> Install
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={dismiss}
                data-testid="install-prompt-dismiss"
                className="rounded-full text-slate-400 hover:text-slate-200 hover:bg-white/5"
              >
                Not now
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="font-display text-base font-semibold text-slate-100">Add to Home Screen</div>
            <ol className="text-slate-300 text-xs mt-2 space-y-1.5">
              <li className="flex items-center gap-2">
                <span className="text-indigo-300 font-mono-ui">1.</span>
                Tap <Share2 className="w-3 h-3 text-indigo-300 inline mx-0.5" /> Share in Safari
              </li>
              <li className="flex items-center gap-2">
                <span className="text-indigo-300 font-mono-ui">2.</span>
                Choose <Plus className="w-3 h-3 text-indigo-300 inline mx-0.5" /> "Add to Home Screen"
              </li>
              <li className="flex items-center gap-2">
                <span className="text-indigo-300 font-mono-ui">3.</span>
                Tap Add — done.
              </li>
            </ol>
            <div className="mt-3 flex justify-end">
              <Button size="sm" variant="ghost" onClick={dismiss} className="rounded-full text-slate-400 hover:text-slate-200 hover:bg-white/5">
                Got it
              </Button>
            </div>
          </>
        )}
      </div>
      <button
        onClick={dismiss}
        data-testid="install-prompt-close"
        aria-label="Dismiss"
        className="text-slate-500 hover:text-slate-200 transition shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
